import React, { useRef, useState } from 'react'
import { Col, Container, Row } from 'react-grid-system'
import { useForm } from 'react-hook-form'
import { TailSpin } from 'react-loader-spinner'
import { Link } from 'react-router-dom'

import siteContent from '../../../../content/site.json'
import addresses from '../../../../data/footer'
import SuccessModal from '../../../../shared/components/SuccessModal/SuccessModal'
import useNavigateWithGTM from '../../../../shared/hooks/useNavigateWithGTM'
import { pushToGTM } from '../../../../shared/utis/gtm'
import { submitNewsletterToZoho } from '../../../../utils/api'
import BottomFooter from './BottomFooter'
import styles from './styles.module.scss'

type SocialPlatform = 'linkedin' | 'youtube'

type SocialLink = { platform: SocialPlatform; label: string; url: string }

const socialIcons: Record<SocialPlatform, string> = {
  linkedin: require('../../../../../public/images/linkedin.svg'),
  youtube: require('../../../../../public/images/youtube.svg'),
}

const socialTracking: Record<SocialPlatform, { event: string; tagName: string }> = {
  linkedin: { event: 'linkedin_icon', tagName: 'LinkedIn icon' },
  youtube: { event: 'youtube_link', tagName: 'YouTube link' },
}

const footerContent = siteContent.footer
const socialLinks = (footerContent.socialLinks as SocialLink[]).filter(
  (link) => link.url && socialIcons[link.platform]
)

const Footer = ({ isMinimized = false }: { isMinimized?: boolean }) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const { navigateToExternalResourceGTM } = useNavigateWithGTM()

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<{ mail: string }>()
  const [isLoad, setIsLoad] = useState(false)
  const isSubmittingRef = useRef(false)
  const [newsletterError, setNewsletterError] = useState('')
  const { navigateWithGTM } = useNavigateWithGTM()

  const submitNewsletter = async ({ mail }: { mail: string }) => {
    if (isSubmittingRef.current) return

    isSubmittingRef.current = true
    setIsLoad(true)
    setNewsletterError('')

    try {
      await submitNewsletterToZoho({ email: mail })
      reset()
      setIsModalOpen(true)
      pushToGTM({ event: 'newsletter_sign_up', tagName: 'Newsletter sign-up' })
    } catch {
      setNewsletterError('We couldn’t process your subscription. Please try again.')
    } finally {
      isSubmittingRef.current = false
      setIsLoad(false)
    }
  }

  return (
    <footer
      className={styles.footer}
      style={{
        ...(isMinimized && { paddingTop: 10, paddingBottom: 30 }),
      }}
    >
      <Container>
        {!isMinimized ? (
          <Row className={styles.footer_full}>
            <Col md={5} className={styles.section}>
              <div className={styles.logo_section}>
                <img
                  src={require('../../../../../public/images/logo_white.svg')}
                  alt="Urus Advisory"
                />
                <p>{footerContent.tagline}</p>
                <a href={`mailto:${footerContent.email}`}>
                  <b>{footerContent.emailLabel}</b>
                </a>
              </div>
              <div className={styles.newsletter_section}>
                <p className={styles.newsletter_section_title}>{footerContent.newsletterTitle}</p>
                <p>{footerContent.newsletterText}</p>
                <form
                  className={styles.sign_up}
                  onSubmit={(event) => {
                    void handleSubmit(submitNewsletter)(event)
                  }}
                  noValidate
                >
                  <input
                    disabled={isLoad || isModalOpen}
                    type="email"
                    placeholder="Company Email"
                    {...register('mail', {
                      required: true,
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'invalid email address',
                      },
                    })}
                    style={{
                      border: errors.mail ? '1px solid #ff000073' : '1px solid #484581',
                      background: errors.mail ? 'rgba(255, 255, 255, 0)' : 'rgba(255, 255, 255, 0)',
                      marginRight: '8px',
                    }}
                  />
                  <button type="submit" disabled={isLoad || isModalOpen}>
                    SIGN UP
                    {isLoad && (
                      <TailSpin
                        height="25"
                        width="25"
                        color="#fff"
                        ariaLabel="line-wave-loading"
                        wrapperClass={styles.loader}
                        wrapperStyle={{}}
                      />
                    )}
                  </button>
                </form>
                {newsletterError && (
                  <p className={styles.newsletter_error} role="alert">
                    {newsletterError}
                  </p>
                )}
              </div>
              <div className={styles.follow_us}>
                <b>{footerContent.followUsTitle}</b>
                <div className={styles.follow_us__links}>
                  {socialLinks.map((link) => (
                    <a
                      key={link.platform + link.url}
                      href={link.url}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(e) => {
                        navigateToExternalResourceGTM(e, link.url, socialTracking[link.platform])
                      }}
                    >
                      <img
                        className={styles.section_icon}
                        src={socialIcons[link.platform]}
                        alt=""
                      />
                      <span>{link.label}</span>
                    </a>
                  ))}
                </div>
              </div>
            </Col>
            <Col md={3} className={styles.section}>
              <Row>
                <Col>
                  <div className={styles.navigation_section}>
                    <h5>Navigation</h5>
                    <Link
                      to="/services"
                      onClick={(e) => {
                        e.preventDefault()
                        navigateWithGTM('/services', { event: 'services' })
                      }}
                    >
                      Services
                    </Link>
                    <Link
                      to="/blog"
                      onClick={(e) => {
                        e.preventDefault()
                        navigateWithGTM('/blog', { event: 'blog' })
                      }}
                    >
                      Insights
                    </Link>
                    <Link
                      to="/about"
                      onClick={(e) => {
                        e.preventDefault()
                        navigateWithGTM('/about', { event: 'about' })
                      }}
                    >
                      About Us
                    </Link>
                    <Link
                      to="/contact"
                      onClick={(e) => {
                        e.preventDefault()
                        navigateWithGTM('/contact', { event: 'contact' })
                      }}
                    >
                      Contact
                    </Link>
                  </div>
                </Col>
              </Row>
            </Col>
            <Col md={4}>
              <Row>
                <Col>
                  <div className={styles.location_section}>
                    <h5>Office locations</h5>
                  </div>
                </Col>
              </Row>
              <Row>
                <Col md={12} className={styles.section}>
                  {addresses.col1.map((address) => {
                    return (
                      <div
                        key={address.country + address.line1}
                        className={`${styles.location_section} ${styles.location_section_items}`}
                      >
                        <b>{address.country}</b>
                        <p>
                          {address.line1} {address.line2}
                        </p>
                      </div>
                    )
                  })}
                </Col>
              </Row>
            </Col>
          </Row>
        ) : null}
        <BottomFooter />
      </Container>
      <SuccessModal
        isOpen={isModalOpen}
        handleModal={setIsModalOpen}
        message="Thank you for subscribing to the Urus Advisory newsletter."
      />
    </footer>
  )
}

export default Footer
